// Export emails as a csv file

import type { fetchedUser } from "@/types/fetchedUser";
import type { AcContactData, AcMilestone, AcPotential, AcTrackedPotential } from "@/types/crm";
import type { AcComment, AcReminder, LastUpdate } from "@/types/application";

import firebase from '../firebase'
import { PotentialEMail, EMailTag } from "@/types";
import { ref, computed, onBeforeMount, onBeforeUnmount } from "vue";
import { useStore } from 'vuex';
import { formatDate, formatUser } from "@/mixins/visualFormatters";
import { postData } from "@/mixins/modularFirebaseOperations";
import * as XLSX from 'xlsx/xlsx.mjs';


// Composable to multi-select emails on the table

const selectedEMails = ref<string[]>([])

export const useMultiSelectEMails = () => {
    return { selectedEMails }
}

export const resetSelectedEMails = () => {
    selectedEMails.value = []
}

// Add tags to multiple emails

export const addTagToMultipleEMails = async (tag: EMailTag, localEmailArray: PotentialEMail[]) => {
    const emailsRef = firebase.firestore().collection("crm/sales/EMailList");

    for (const emailId of selectedEMails.value) {
        try {
            const docRef = emailsRef.doc(emailId)
            docRef.update({
                tags: firebase.firestore.FieldValue.arrayUnion(tag),
            });

            const email = localEmailArray.find((email) => email.id === emailId);
            if (email) {
                if (!email.tags?.includes(tag)) {
                    email.tags?.push(tag);
                }
            }
        } catch (error) {
            console.error("Error updating document: ", error);
        }

    }
}

export const useGetAllEmails = () => {
    const emailList = ref<PotentialEMail[]>([]);
    const isLoadingEmails = ref<boolean>(false);

    const getData = async () => {
        isLoadingEmails.value = true;
        const emailsRef = firebase.firestore().collection("crm/sales/EMailList");
        try {
            emailsRef.orderBy("created", "desc").get().then((querySnapshot) => {
                querySnapshot.forEach((doc) => {
                    emailList.value.push({
                        id: doc.id,
                        desc: doc.data().desc,
                        firstName: doc.data().firstName,
                        lastName: doc.data().lastName,
                        industry: doc.data().industry,
                        email: doc.data().email,
                        phone: doc.data().phone,
                        company: doc.data().company,
                        companyUrl: doc.data().companyUrl,
                        tags: doc.data().tags,
                        sentBy: doc.data().sentBy,
                    })
                });
            });
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        isLoadingEmails.value = false;
    }

    // Call getData function when the component is mounted

    getData();
    return { emailList, isLoadingEmails };
};


export const exportEmailsToCSV = async (emails: PotentialEMail[]) => {


    const exportedEmails = emails.map(email => {
        return {
            email: email.email,
            firstName: email.firstName,
            lastName: email.lastName,
            phone: email.phone,
            company: email.company?.replace(/,/g, ''),
            industry: email.industry,
        }
    })

    const exportEmailsToCSV = exportedEmails.map(email => {
        return `${email.email},${email.firstName},${email.lastName},${email.phone},${email.company},${email.industry}`
    })

    const headers = "email,firstName,lastName,phone,company,industry, \n"

    const csvContent = "data:text/csv;charset=utf-8," + headers + exportEmailsToCSV.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "emails.csv");
    document.body.appendChild(link); // Required for FF

    link.click();
}

export const exportEmailsToExcel = async (emails: PotentialEMail[]) => {
    // Prepare the data for the worksheet
    const data = emails.map(email => ({
        "E Posta Adresi": email.email,
        "Adı": email.firstName,
        "Soyadı": email.lastName,
        "Telefon Numarası": email.phone,
        "Şirketi": email.company?.replace(/,/g, ''),
        "Şirket Web Sitesi": email.companyUrl,
        "Sektör": email.industry,
        "Açıklama": email.desc,
    }));

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Create a new workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Emails");

    // Generate buffer
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob and link to download
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'emails.xlsx';

    // Append to the DOM and trigger download

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const massImportEmails = async (file: File, tags: EMailTag[] | [], existingEmails: PotentialEMail[], userName: string) => {
    const user = userName
    const addedTags = tags ? tags : []
    const created = new Date()

    // Return and throw error if file is not a csv, xls, or xlsx
    if (!file.name.endsWith(".csv") && !file.name.endsWith(".xls") && !file.name.endsWith(".xlsx")) {
        return alert("Please upload a csv, xls, or xlsx file");
    } else if (file.name.endsWith(".csv")) {
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = async () => {
            const csv = reader.result;
            const allLines = csv?.toString().split("\n") || [];

            // Remove empty lines
            const lines = allLines.filter(line => line.trim() !== "");

            // Map each line to a new object, removing the first line (headers)
            const emails = lines.slice(1).map(line => {
                const data = line.split(",");
                if (data.length < 1 || data[0] === '') return null; // Adjust as needed
                return {
                    email: data[0],
                    firstName: data[1] || "",
                    lastName: data[2] || "",
                    phone: data[3] || "",
                    company: data[4] || "",
                    companyUrl: data[5] || "",
                    industry: data[6] || "",
                    tags: addedTags,
                    sentBy: user,
                    created: created,
                    desc: "",
                };
            }).filter(email => email !== null);


            const addedEmails = excludeExistingEmails(emails, existingEmails)

            const emailsRef = firebase.firestore().collection("crm/sales/EMailList");

            // Create a new batch

            let batch = firebase.firestore().batch();
            let batchCount = 0;


            for (const email of addedEmails) {
                if (!email.email) {
                    continue;
                } else {
                    try{
                        const docRef = (emailsRef).doc(); // Create a new document reference
                        batch.set(docRef, email); // Add to batch
                        batchCount++;
                    } catch(error){
                        console.error(error)
                    }

                }

                // Commit and start a new batch if limit is reached
                
                if (batchCount === 500) {
                    await batch.commit();
                    batch = firebase.firestore().batch();
                    batchCount = 0;
                }
            }

            // Commit any remaining operations in the final batch
            if (batchCount > 0) {
                await batch.commit();
            } 
        };




    } else if (file.name.endsWith(".xls") || file.name.endsWith(".xlsx")) {

        const excelData: any = await convertExcelFileToJSON(file);
        const isExcelDataValid = validateExcelData(excelData);

        if (!isExcelDataValid) {
            return alert("Yüklediğiniz excel dosyası beklenen formatta değil. Lütfen excel dosyanızı kontrol edin ve tekrar deneyin.");
        }

        const mappedEmails = excelData.map((email) => {
            return {
                email: email["E-Posta Adresi"],
                firstName: email["Adı"] ?? "",
                lastName: email["Soyadı"] ?? "",
                phone: email["Telefon Numarası"] ? email["Telefon Numarası"].toString() : "",
                company: email["Şirketi"] ?? "",
                companyUrl: email["Şirket Web Sitesi"] ?? "",
                industry: email["Sektör"] ?? "",
                tags: addedTags,
                sentBy: user,
                created: new Date(),
                desc: email["Açıklama"] ?? "",
            };
        })

        // Exclude emails that are already in the database

        const addedEmails = excludeExistingEmails(mappedEmails, existingEmails)


        const emailsRef = firebase.firestore().collection("crm/sales/EMailList");
        let batch = firebase.firestore().batch();
        let batchCount = 0;

        addedEmails.forEach(async (email) => {
            const docRef = emailsRef.doc(); // Create a new document reference
            batch.set(docRef, email);
            batchCount++;

            // Commit and start a new batch if limit is reached

            if (batchCount === 500) {
                batch.commit();
                batch = firebase.firestore().batch();
                batchCount = 0;
            }

        })

        if (batchCount > 0) {
            await batch.commit();
        }
    }

    // If there are no errors, let the user know the import was successful and refresh the page.

}

function convertExcelFileToJSON(file: File) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result as ArrayBufferLike);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);

                resolve(json); // Resolve the promise with the JSON data
            } catch (error) {
                reject(error); // Reject the promise if there's an error
            }
        };

        reader.onerror = (error) => {
            reject(error); // Also reject the promise if FileReader encounters an error
        };

        reader.readAsArrayBuffer(file);
    });
}

// Check the JSON keys to ensure they match the expected format.
function validateExcelData(data: any[]) {
    const expectedKeys = ['E-Posta Adresi', 'Adı', 'Soyadı', 'Telefon Numarası', 'Şirketi', 'Şirket Web Sitesi', 'Sektör', 'Açıklama'];
    const keys = Object.keys(data[0]);

    return keys.every((key) => expectedKeys.includes(key));
}

// Exclude emails that are already in the database: 

function excludeExistingEmails(addedEmails: any[], existingEmails: PotentialEMail[]) {
    const existingEmailsArray = existingEmails.map(email => email.email)
    const emailsNotOnLocal =  addedEmails.filter(email => !existingEmailsArray.includes(email.email))

    // Remove duplicates

    const uniqueEmails = Array.from(new Set(emailsNotOnLocal.map(email => email.email)))
        .map(email => {
            return emailsNotOnLocal.find(e => e.email === email)
        })

    return uniqueEmails
}

export const movePotentialToTracked = async (potential: AcPotential, userProfile: fetchedUser) => {
    if(!potential.id) {
        alert("Potansiyel bulunamadı.")
        return
    }

    const trackedPotential: AcTrackedPotential = {
        ...potential,
        milestones: [],
        evaluation: "Satış süreçleri, teknik konulara ve gerçek ihtiyaçlara dayandığı kadar müşterilerin kişisel özelliklerine, onları ne kadar tanıdığınıza ve müşterilerin size karşı yaklaşımına dayanır. Bu bölümde, müşterinin kişisel özellikleri, beklentileri ve bugüne kadar yaşanan temaslardaki tavırları konusunda detaylı bilgiler girebilirsiniz.",
        trackedAt: new Date(),
        trackedBy: formatUser(userProfile),
        priority: "STANDARD",
    }
    try {
        const db = firebase.firestore()
        
        await db.collection("crm/tracked/list").doc(potential.id!).set(trackedPotential)
        
        // Add the trackedBy user to assignedTo array
        await db.collection("crm/tracked/list").doc(potential.id!).update({
            assignedTo: firebase.firestore.FieldValue.arrayUnion(trackedPotential.trackedBy)
        })
        await db.collection("crm/potentials/list").doc(potential.id!).delete()
        
    } catch (error) {
        alert(`Taşıma işlemi sırasında bir hata oluştu: ${error}. Lütfen bu mesajı yazılım ekibine bildiriniz.`)
    }
}

// Get a single potential real-time.

export const useFetchRealTimePotential = (id: string) => {
    console.log("Fetching potential", id)
    const potential = ref<AcPotential | undefined>(undefined)
    const isLoadingPotential = ref<boolean>(false)

    let unsubscribePotential: () => void

    const setupPotentialListener = async () => {
        isLoadingPotential.value = true
        unsubscribePotential = firebase.firestore()
            .doc(`crm/potentials/list/${id}`)
            .onSnapshot(
                (doc) => {
                    if (doc.exists) {
                        potential.value = {
                            ...doc.data() as AcPotential,
                            id: doc.id,
                        }
                    }
                    isLoadingPotential.value = false
                },
                (error) => {
                    console.error('Error fetching potential:', error)
                    isLoadingPotential.value = false
                }
            )
        return unsubscribePotential
    }

    onBeforeMount(async () => {
        unsubscribePotential = await setupPotentialListener()
    })

    onBeforeUnmount(() => {
        unsubscribePotential?.()
    })
   

    return { potential, isLoadingPotential }
}


/* Get a single tracked potential to work on it real time. 

Currently, this function seems very similar to the one above. But there is a chance that it will have unique needs later on.

*/

export const useFetchRealTimeTrackedPotential = (id: string) => {
    console.log("Fetching tracked potential", id)
    const trackedPotential = ref<AcTrackedPotential | undefined>(undefined)
    const isLoadingTrackedPotential = ref<boolean>(false)

    let unsubscribeTrackedPotential: () => void

    const setupTrackedPotentialListener = async () => {
        isLoadingTrackedPotential.value = true
        unsubscribeTrackedPotential = firebase.firestore()
            .doc(`crm/tracked/list/${id}`)
            .onSnapshot(
                (doc) => {
                    if (doc.exists) {
                        trackedPotential.value = {
                            ...doc.data() as AcTrackedPotential,
                            id: doc.id,
                        }
                    }
                    isLoadingTrackedPotential.value = false
                },
                (error) => {
                    console.error('Error fetching tracked potential:', error)
                    isLoadingTrackedPotential.value = false
                }
            )
        return unsubscribeTrackedPotential
    }

    onBeforeMount(async () => {
        unsubscribeTrackedPotential = await setupTrackedPotentialListener()
    })

    onBeforeUnmount(() => {
        unsubscribeTrackedPotential?.()
    })
   

    return { trackedPotential, isLoadingTrackedPotential }
}


export const reactivatePotential = async (potential: AcPotential) => {
    if (!potential.id) {
        alert("Potansiyel bulunamadı.")
        return
    }

    try {
        const db = firebase.firestore()
        
        // Update status to ACTIVE
        await db.collection("crm/potentials/list").doc(potential.id).update({
            status: "ACTIVE",
            reactivatedAt: new Date(),
        })
        
        console.log("Potansiyel başarıyla aktif hale getirildi.")
        
    } catch (error) {
        alert(`Aktif hale getirme işlemi sırasında bir hata oluştu: ${error}. Lütfen bu mesajı yazılım ekibine bildiriniz.`)
    }
}

export const reactivateTrackedPotential = async (trackedPotential: AcTrackedPotential) => {
    if (!trackedPotential.id) {
        alert("Takip edilen potansiyel bulunamadı.")
        return
    }

    try {
        const db = firebase.firestore()
        
        // Update status to ACTIVE
        await db.collection("crm/tracked/list").doc(trackedPotential.id).update({
            status: "ACTIVE",
            reactivatedAt: new Date(),
        })
        
        console.log("Takip edilen potansiyel başarıyla aktif hale getirildi.")
        
    } catch (error) {
        alert(`Aktif hale getirme işlemi sırasında bir hata oluştu: ${error}. Lütfen bu mesajı yazılım ekibine bildiriniz.`)
    }
}

export const postCRMReminder = async (additionalMessage: string, reminderDate: string, potential: AcPotential, potentialLocation: string) => {

    const standardMessage = `Merhaba,

    Bu mesaj, CRM sistemimiz üzerinden ${potential.title} fırsatı ile ilgili bir hatırlatma oluşturulduğu ve bu fırsata sorumlu olarak eklendiğiniz için gönderildi.

    Varsa hatırlatma için oluşturulan ek mesajı aşağıda görebilirsiniz:

    ${additionalMessage}

    Sistem üzerinde belgeye ulaşmak için, lütfen aşağıdaki linke tıklayın:

    https://dash.dansuk.com.tr/crm/potential-detail/${potential.id}

    Eğer hatırlatmayı siz oluşturmadıysanız, lütfen bu mesajı görmezden gelin.

    İyi çalışmalar,

    Dansuk Mühendislik
    
    `


    const reminderObj: AcReminder = {
        date: reminderDate,
        standardMessage,
        additionalMessage,
        relatedId: potential.id,
        to: potential.assignedTo?.map((user: fetchedUser) => user.emailAddress) || [],
        isSent: false,
    }

    await postData(
        reminderObj,
        "crm/reminders/list",
    )
}

// AI features

const mapTrackedPotentialToJSON = async (potential: AcTrackedPotential) => {


    function convertTimestamp(timestamp: any): string {
        if (timestamp?.toDate) {
          // Already a Firebase Timestamp instance
          return timestamp.toDate().toISOString();
        }
        if (timestamp?.seconds !== undefined) {
          // Plain object with seconds/nanoseconds
          return new firebase.firestore.Timestamp(
            timestamp.seconds, 
            timestamp.nanoseconds
          ).toDate().toISOString();
        }
        return timestamp;
      }

    const formatUserDataForAPI = (user: fetchedUser) => {
        return {
            id: user.id,
            user_name: user.userName,
            email_address: user.emailAddress,
            profile_image: user.profileImage,
        }
    }
    
    const formatContactDataForAPI = (contact: AcContactData) => {
        return {
            title: contact.title,
            email_address: contact.emailAddress,
            phone_number: contact.phoneNumber,
            address: contact.address,
            website: contact.website,
        }
    }

    const formatUpdateDataForAPI = (update: LastUpdate) => {
        return {
            date: convertTimestamp(update.date),
            user: formatUserDataForAPI(update.user),
        }
    }

    const formatMilestoneForAPI = (milestone: AcMilestone) => {
        return {
            title: milestone.title,
            category: milestone.category,
            explanation: milestone.explanation,
            icon: milestone.icon,
            date: convertTimestamp(milestone.date),
            user: formatUserDataForAPI(milestone.user),
        }
    }

    const formatCommentForAPI = (comment: AcComment) => {
        return {
            comment: comment.comment,
            date: convertTimestamp(comment.date),
            user: formatUserDataForAPI(comment.user),
            is_automatic: comment.isAutomatic || null,
        }
    }


    const potentialObject = {
        project_id: potential.id,
        title: potential.title,
        description: potential.description,
        potential_type: potential.type,
        potential_rating: potential.potentialRating,
        potential_categories: potential.potentialCategories.map((category) => category.name),
        contact_information: potential.contact.map(formatContactDataForAPI),
        created_at: convertTimestamp(potential.createdAt),
        updated_at: formatUpdateDataForAPI(potential.updatedAt),
        added_by: formatUserDataForAPI(potential.addedBy),
        referred_by: formatUserDataForAPI(potential.referredBy),
        third_party_refferrer: potential.thirdPartyRefferrer,
        status: potential.status,
        assigned_to: potential.assignedTo?.map(formatUserDataForAPI),
        comments: potential.comments?.map(formatCommentForAPI),
        milestones: potential.milestones.map(formatMilestoneForAPI),
        evaluation: potential.evaluation,
        tracked_at: convertTimestamp(potential.trackedAt),
        tracked_by: formatUserDataForAPI(potential.trackedBy),
        priority: potential.priority,
    }

    return JSON.stringify(potentialObject)
}


const baseUrl = "https://ai.dansuk.com.tr"
//

export const getStrategyResponse = async (potential: AcTrackedPotential) => {
    const potentialObject = await mapTrackedPotentialToJSON(potential)
    const response = await fetch(
        baseUrl + "/crm/craft-strategy/",
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: potentialObject,
        }
    )

}

export const getActionMapResponse = async (potential: AcTrackedPotential) => {
    const potentialObject = await mapTrackedPotentialToJSON(potential)
    const response = await fetch(
        baseUrl + "/crm/craft-action-map/",
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: potentialObject,
        }
    )

}